import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PricingDatePicker = ({ onDateSelect, selectedLocation, onOpenChange }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const pickerRef = useRef(null);

  // Detect if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 580);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Notify parent when open state changes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  // Close picker when clicking outside (desktop only) - also save dates
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobile && pickerRef.current && !pickerRef.current.contains(event.target)) {
        // Save dates before closing if both are selected
        if (checkIn && checkOut) {
          onDateSelect(checkIn, checkOut);
        }
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, checkIn, checkOut, onDateSelect]);

  // Prevent body scroll and hide header when mobile modal is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('calendar-modal-open');
      return () => {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('calendar-modal-open');
      };
    }
  }, [isMobile, isOpen]);

  // Progressive loading: 2 visible months (blocking), then next 2 silently; reset only on location change
  const loadedMonthsRef = useRef(new Set());
  const locationGenRef = useRef(0);
  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    setPrices({});
    loadedMonthsRef.current = new Set();
    locationGenRef.current += 1;
  }, [selectedLocation]);

  useEffect(() => {
    if (isOpen) {
      const runProgressive = async () => {
        await fetchPricesForWindow(currentMonth, 2);
        fetchPricesForWindow(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 1), 2, { silent: true });
      };
      runProgressive();
    }
  }, [isOpen, selectedLocation]);

  useEffect(() => {
    if (isOpen) fetchPricesForWindow(currentMonth, 4, { silent: true });
  }, [currentMonth]);

  const fetchPricesForWindow = async (anchorDate, monthsAhead, { silent = false } = {}) => {
    const wanted = [];
    for (let i = 0; i < monthsAhead; i += 1) {
      const m = new Date(anchorDate.getFullYear(), anchorDate.getMonth() + i, 1);
      if (!loadedMonthsRef.current.has(monthKey(m))) wanted.push(m);
    }
    if (wanted.length === 0) return;
    const first = wanted[0];
    const last = wanted[wanted.length - 1];
    const rangeStart = new Date(first.getFullYear(), first.getMonth(), 1);
    const rangeEnd = new Date(last.getFullYear(), last.getMonth() + 1, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (rangeStart < today) rangeStart.setTime(today.getTime());
    if (rangeEnd <= rangeStart) return;
    wanted.forEach((m) => loadedMonthsRef.current.add(monthKey(m)));
    const propertyId = selectedLocation && selectedLocation !== 'all' ? selectedLocation : null;
    const gen = locationGenRef.current;
    if (!silent) setLoading(true);
    try {
      let response;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          response = await axios.get(`${BACKEND_URL}/api/properties/calendar-prices`, {
            params: { start_date: formatDate(rangeStart), end_date: formatDate(rangeEnd), property_id: propertyId },
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
            timeout: 45000,
          });
          break;
        } catch (err) {
          if (attempt === 2 || gen !== locationGenRef.current) throw err;
          await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
        }
      }
      if (gen !== locationGenRef.current) return;
      setPrices((prev) => {
        const next = { ...prev };
        response.data.forEach((item) => { next[item.date] = { price: item.price, available: item.available }; });
        return next;
      });
    } catch (error) {
      if (gen === locationGenRef.current) wanted.forEach((m) => loadedMonthsRef.current.delete(monthKey(m)));
      console.error('Error fetching prices:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDateClick = (date) => {
    const dateStr = formatDate(date);
    const priceInfo = prices[dateStr];
    
    if (!priceInfo || !priceInfo.available) return;

    if (!checkIn || (checkIn && checkOut)) {
      // Start new selection
      setCheckIn(date);
      setCheckOut(null);
      
      // ONLY advance calendar if user clicked on the LAST day of the month
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      if (date.getDate() === lastDayOfMonth) {
        // User clicked on last day - advance calendar to show next month
        const nextMonthDate = new Date(currentMonth);
        nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
        setCurrentMonth(nextMonthDate);
      }
    } else if (checkIn && !checkOut) {
      // Validate that all dates between check-in and this date are available
      if (date > checkIn) {
        // Check all dates in range
        const current = new Date(checkIn);
        current.setDate(current.getDate() + 1); // Start from day after check-in
        let allAvailable = true;
        
        while (current < date) {
          const checkDateStr = formatDate(current);
          const checkPriceInfo = prices[checkDateStr];
          
          if (!checkPriceInfo || !checkPriceInfo.available) {
            allAvailable = false;
            break;
          }
          
          current.setDate(current.getDate() + 1);
        }
        
        if (allAvailable) {
          // All dates available - complete selection
          setCheckOut(date);
        } else {
          // Some dates unavailable - restart selection with new date as check-in
          setCheckIn(date);
          setCheckOut(null);
        }
      } else {
        // Clicked date is before check-in - restart selection
        setCheckIn(date);
        setCheckOut(null);
      }
    }
  };

  const handleConfirm = () => {
    if (checkIn && checkOut) {
      onDateSelect(checkIn, checkOut);
      setIsOpen(false);
    }
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isDateInRange = (date) => {
    if (!checkIn || !checkOut) return false;
    return date > checkIn && date < checkOut;
  };

  const isCheckIn = (date) => {
    return checkIn && formatDate(date) === formatDate(checkIn);
  };

  const isCheckOut = (date) => {
    return checkOut && formatDate(date) === formatDate(checkOut);
  };

  const renderMonth = (monthOffset) => {
    const month = new Date(currentMonth);
    month.setMonth(month.getMonth() + monthOffset);
    
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    // Get month name based on current i18n language
    const localeMap = {
      'hr': 'hr-HR',
      'en': 'en-US',
      'it': 'it-IT',
      'de': 'de-DE'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    const monthName = month.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();
    // Get day of week, adjusted for Monday start (0=Mon, 6=Sun)
    let startingDayOfWeek = firstDay.getDay();
    // Convert Sunday=0 to Sunday=6 for Monday-first week
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const weeks = [];
    let currentWeek = [];
    
    // Fill in empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      currentWeek.push(null);
    }
    
    // Fill in the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    // Fill in remaining empty cells
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }
    
    return (
      <div className="w-full">
        {/* Month header with navigation arrows inline */}
        <div className="flex items-center justify-between mb-2">
          {/* Left arrow - only show for first month */}
          {monthOffset === 0 && (
            <button
              onClick={previousMonth}
              data-testid="calendar-prev-month"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {monthOffset !== 0 && <div className="w-9 sm:w-10" />}
          
          {/* Month name */}
          <h3 className="text-center font-semibold text-3xl sm:text-2xl flex-1">{monthName}</h3>
          
          {/* Right arrow - show for last visible month */}
          {(monthOffset === 0 && isMobile) || (monthOffset === 1 && !isMobile) ? (
            <button
              onClick={nextMonth}
              data-testid="calendar-next-month"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <div className="w-9 sm:w-10" />
          )}
        </div>
        
        {/* Day headers - Monday first, translated */}
        <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
          {(() => {
            // Day names starting from Monday
            const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
            const dayNames = {
              hr: ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'],
              en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
              de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
              it: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
            };
            const currentLang = i18n.language || 'en';
            const days = dayNames[currentLang] || dayNames['en'];
            
            return days.map((day, idx) => (
              <div key={dayKeys[idx]} className="text-center text-[10px] sm:text-xs font-medium text-gray-600 py-0.5">
                {day}
              </div>
            ));
          })()}
        </div>
        
        {/* Date grid */}
        <div className="space-y-0.5 sm:space-y-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {week.map((date, dayIndex) => {
                if (!date) {
                  return <div key={dayIndex} className="aspect-square" />;
                }
                
                const dateStr = formatDate(date);
                const priceInfo = prices[dateStr] || { price: 0, available: false };
                const isAvailable = priceInfo.available;
                const inRange = isDateInRange(date);
                const isStart = isCheckIn(date);
                const isEnd = isCheckOut(date);
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                
                return (
                  <div
                    key={dayIndex}
                    onClick={() => handleDateClick(date)}
                    data-testid={`calendar-day-${formatDate(date)}`}
                    onMouseEnter={() => setHoveredDate(date)}
                    onMouseLeave={() => setHoveredDate(null)}
                    className={`
                      aspect-square flex flex-col items-center justify-center cursor-pointer
                      relative overflow-hidden
                      ${!isAvailable || isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'}
                      ${isAvailable && !isPast && !isStart && !isEnd ? 'hover:bg-gray-50' : ''}
                      border border-gray-200 text-[11px] sm:text-xs
                    `}
                  >
                    {/* In-range background - light fill between start and end */}
                    {inRange && !isStart && !isEnd && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{ backgroundColor: 'var(--color-calendar-selection)', opacity: 0.35 }}
                      />
                    )}
                    
                    {/* Check-in triangular indicator - bottom-right corner filled */}
                    {isStart && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `linear-gradient(to bottom right, transparent 50%, var(--color-calendar-selection) 50%)`
                        }}
                      />
                    )}
                    
                    {/* Check-out triangular indicator - top-left corner filled */}
                    {isEnd && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `linear-gradient(to top left, transparent 50%, var(--color-calendar-selection) 50%)`
                        }}
                      />
                    )}
                    
                    {/* Date number */}
                    <span className="relative z-10 font-medium text-[11px] sm:text-xs">
                      {date.getDate()}
                    </span>
                    
                    {/* Price */}
                    {isAvailable && !isPast && (
                      <span 
                        className="relative z-10 font-medium text-[9px] sm:text-[10px]"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        €{priceInfo.price}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const previousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  return (
    <div className="relative" ref={pickerRef}>
      {/* Input field - styled to match location/guests selectors */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        data-testid="date-picker-trigger"
        className="w-full h-14 px-4 flex items-center justify-between border-2 rounded-lg cursor-pointer bg-white hover:border-[var(--color-primary)] transition-all"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {checkIn && checkOut ? (
            <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text-primary)' }}>
              {formatDisplayDate(checkIn)} - {formatDisplayDate(checkOut)}
            </span>
          ) : (
            <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)', color: 'var(--color-text)' }}>{t('home.selectDates')}</span>
          )}
        </div>
        <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Calendar popup - FULL SCREEN on mobile */}
      {isOpen && (
        <>
          {/* Full screen overlay for mobile */}
          {isMobile && (
            <div 
              className="fixed top-0 left-0 right-0 bottom-0 bg-white overflow-y-auto"
              style={{ zIndex: 99999, position: 'fixed', width: '100vw', height: '100vh' }}
            >
              <div className="p-4">
                {/* Mobile header with close button */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                  <h2 className="text-xl font-bold text-[var(--color-text-primary)]">{t('home.selectDates')}</h2>
                  <button
                    onClick={() => {
                      // Save dates if both selected when closing
                      if (checkIn && checkOut) {
                        onDateSelect(checkIn, checkOut);
                      }
                      setIsOpen(false);
                    }}
                    className="p-2 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-6 h-6 text-[var(--color-text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading prices...</div>
                ) : (
                  <>
                    {/* Single month view on mobile */}
                    <div className="grid gap-4 mb-4 grid-cols-1">
                      {renderMonth(0)}
                    </div>

                    {/* Selection summary */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex flex-col gap-4 mb-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide mb-1" style={{color: 'var(--color-text-muted)'}}>{t('home.calendar.checkIn')}</p>
                            <p className="font-medium text-sm" style={{color: 'var(--color-text-primary)'}}>{checkIn ? formatDisplayDate(checkIn) : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide mb-1" style={{color: 'var(--color-text-muted)'}}>{t('home.calendar.checkOut')}</p>
                            <p className="font-medium text-sm" style={{color: 'var(--color-text-primary)'}}>{checkOut ? formatDisplayDate(checkOut) : '-'}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide mb-1" style={{color: 'var(--color-text-muted)'}}>{t('booking.nights')}</p>
                            <p className="font-medium text-sm" style={{color: 'var(--color-text-primary)'}}>{checkIn && checkOut ? Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24)) : 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Confirm button */}
                    <button
                      onClick={() => {
                        if (checkIn && checkOut) {
                          onDateSelect(checkIn, checkOut);
                        }
                        setIsOpen(false);
                      }}
                      disabled={!checkIn || !checkOut}
                      className="w-full py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: checkIn && checkOut ? 'var(--color-primary)' : '#ccc', 
                        color: 'white' 
                      }}
                    >
                      {t('common.confirm')}
                    </button>

                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-600 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border border-gray-200 bg-white" />
                        <span>{t('home.calendar.available')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border border-gray-200 bg-gray-100" />
                        <span>{t('home.calendar.notAvailable')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, transparent 50%, var(--color-calendar-selection) 50%)' }} />
                        </div>
                        <span>{t('home.calendar.checkIn')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top left, transparent 50%, var(--color-calendar-selection) 50%)' }} />
                        </div>
                        <span>{t('home.calendar.checkOut')}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Desktop dropdown */}
          {!isMobile && (
            <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl z-[100] w-full max-w-full overflow-hidden p-4">
              {/* Calendar months */}
              {loading ? (
                <div className="text-center py-8 text-gray-500">Loading prices...</div>
              ) : (
                <div className="grid gap-4 mb-4 grid-cols-2">
                  {renderMonth(0)}
                  {renderMonth(1)}
                </div>
              )}

              {/* Selection summary */}
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-200">
                <div className="flex gap-6">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('booking.checkIn')}</div>
                    <div className="font-medium text-sm">{checkIn ? formatDisplayDate(checkIn) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('booking.checkOut')}</div>
                    <div className="font-medium text-sm">{checkOut ? formatDisplayDate(checkOut) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('properties.nights')}</div>
                    <div className="font-medium text-sm">{calculateNights()}</div>
                  </div>
                </div>
                
                <button
                  onClick={handleConfirm}
                  disabled={!checkIn || !checkOut}
                  className={`px-6 py-3 font-bold transition-colors text-sm uppercase rounded-md ${
                    checkIn && checkOut
                      ? 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t('common.confirm')}
                </button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 border border-gray-200 bg-white" />
                  <span>{t('home.calendar.available')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 border border-gray-200 bg-gray-100" />
                  <span>{t('home.calendar.notAvailable')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, transparent 50%, var(--color-calendar-selection) 50%)' }} />
                  </div>
                  <span>{t('home.calendar.checkIn')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top left, transparent 50%, var(--color-calendar-selection) 50%)' }} />
                  </div>
                  <span>{t('home.calendar.checkOut')}</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PricingDatePicker;
